import { store } from 'store/store'

export interface PendingMutation {
  id: string
  type: 'riskCreate' | 'fieldUpsert'
  variables: any
  tempId?: string
  dependsOn?: string
  timestamp: number
}

export interface OfflineQueueState {
  /** Owner of this queue — prevents cross-user data leakage */
  userId: string
  mutations: PendingMutation[]
  tempIdMappings: Record<string, string>
  fieldValueMappings: Record<string, string>
}

const OFFLINE_QUEUE_KEY_PREFIX = 'offline-mutations-queue'

const emptyState = (userId: string): OfflineQueueState => {
  return {
    userId,
    mutations: [],
    tempIdMappings: {},
    fieldValueMappings: {}
  }
}

export class OfflineStorageService {
  private static instance: OfflineStorageService
  private listeners: Set<() => void> = new Set()

  static getInstance(): OfflineStorageService {
    if (!OfflineStorageService.instance) {
      OfflineStorageService.instance = new OfflineStorageService()
    }
    return OfflineStorageService.instance
  }

  private getCurrentUserId(): string | null {
    return (store.getState() as any).currentUser?.data?.id ?? null
  }

  private getStorageKey(userId: string): string {
    return `${OFFLINE_QUEUE_KEY_PREFIX}:${userId}`
  }

  private loadState(): OfflineQueueState | null {
    const userId = this.getCurrentUserId()

    if (!userId) {
      return null
    }

    try {
      const stored = localStorage.getItem(this.getStorageKey(userId))

      if (!stored) {
        return emptyState(userId)
      }

      const parsed: OfflineQueueState = JSON.parse(stored)

      // Discard queue if it was created by a different user (stale key collision)
      if (parsed.userId !== userId) {
        localStorage.removeItem(this.getStorageKey(userId))
        return emptyState(userId)
      }

      return parsed
    } catch (error) {
      console.error('Failed to load offline queue:', error)
      return emptyState(userId)
    }
  }

  private setState(state: OfflineQueueState): void {
    try {
      localStorage.setItem(this.getStorageKey(state.userId), JSON.stringify(state))
      this.notifyListeners()
    } catch (error) {
      console.error('Failed to save offline queue:', error)
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener())
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  addMutation(mutation: Omit<PendingMutation, 'id' | 'timestamp'>): void {
    const state = this.loadState()

    if (!state) return

    const newMutation: PendingMutation = {
      ...mutation,
      id: crypto.randomUUID(),
      timestamp: Date.now()
    }

    state.mutations.push(newMutation)
    this.setState(state)
  }

  removeMutation(ids: string[]): void {
    const state = this.loadState()

    if (!state) return

    state.mutations = state.mutations.filter((m) => !ids.includes(m.id))
    this.setState(state)
  }

  clearQueue(): void {
    const userId = this.getCurrentUserId()

    if (!userId) return

    localStorage.removeItem(this.getStorageKey(userId))
    this.notifyListeners()
  }

  updateTempIdMapping(tempId: string, realId: string): void {
    const state = this.loadState()

    if (!state) return

    state.tempIdMappings[tempId] = realId
    this.setState(state)
  }

  updateFieldValueMapping(tempId: string, realId: string): void {
    const state = this.loadState()

    if (!state) return

    state.fieldValueMappings[tempId] = realId
    this.setState(state)
  }

  getMutationsInOrder(): PendingMutation[] {
    const state = this.loadState()

    if (!state) return []

    return [...state.mutations].sort((a, b) => a.timestamp - b.timestamp)
  }

  getState(): OfflineQueueState | null {
    return this.loadState()
  }

  getTempIdMappings(): Record<string, string> {
    return this.loadState()?.tempIdMappings ?? {}
  }

  getFieldValueMappings(): Record<string, string> {
    return this.loadState()?.fieldValueMappings ?? {}
  }
}
